<template>
    <div class="container-fluid">
        <div class="mb-3">
            <BreadcrumbNav />
        </div>
        <!-- Currently Editing Banner -->
        <div v-if="instanceDetail && (instanceDetail.id || instanceDetail.activityname)" class="alert alert-success text-white d-flex align-items-center my-2" role="alert">
            <i class="material-icons me-2">edit</i>
            <div>
                <strong>Currently Editing (Activity Instance):</strong>
                <span v-if="instanceDetail.id" class="ms-2">Instance ID: <strong>{{ instanceDetail.id }}</strong></span>
                <span v-if="instanceDetail.activityname" class="ms-2">- <strong>{{ instanceDetail.activityname }}</strong></span>
            </div>
        </div>

        <!-- Read-Only Banner -->
        <div v-if="readOnly" class="alert alert-warning text-white d-flex align-items-center my-2" role="alert">
            <i class="material-icons me-2">visibility</i>
            <div>
                <strong>View Only:</strong> You do not have permission to edit this activity instance.
            </div>
        </div>

        <!-- Tab Legend -->
        <div class="d-flex align-items-center gap-3 mb-2 text-xs text-secondary">
            <span><i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i> Required</span>
            <span><i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i> Optional</span>
        </div>

        <NavTabs>
            <NavTab target="instance-details" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Instance Details (Required)">
                    Details <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>

            <NavTab target="instance-workgroups" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Instance Workgroups (Required)">
                    Workgroups <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>

            <NavTab target="instance-admins" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Manage Instance Admins (Optional)">
                    Admins <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>
        </NavTabs>

        <ContentContainer>
            <template #content>
                <ExplainPageButton :page-key="`instance.${activeTab}`" />
                <div v-if="activeTab === 'instance-details'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and edit the core settings for this activity instance, including dates, times, and status.</p>
                    <InstanceDetails :readOnly="readOnly" />
                </div>

                <div v-else-if="activeTab === 'instance-workgroups'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and manage the workgroups assigned to this instance.</p>
                    <InstanceWorkgroups :readOnly="readOnly" />
                </div>

                <div v-else-if="activeTab === 'instance-admins'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Assign or remove administrators who can manage this instance.</p>
                    <InstanceAdmins :readOnly="readOnly" />
                </div>
            </template>
        </ContentContainer>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import { usePermissions } from "@/composables/usePermissions"
import { useBreadcrumb } from "@/composables/useBreadcrumb"

// Common layout components
import NavTab from "../../components/Tabs/NavTab.vue"
import NavTabs from "../../components/Tabs/NavTabs.vue"
import ContentContainer from "../../components/PageLayout/ContentContainer.vue"
import BreadcrumbNav from "../../components/common/BreadcrumbNav.vue"
import ExplainPageButton from "@/components/common/ExplainPageButton.vue"

// Instance-related components
import InstanceDetails from "./edit-instance/InstanceDetails.vue"
// import InstanceWorkgroups from "../workgroups/edit-workgroup/InstanceWorkgroups.vue"
import InstanceAdmins from "./edit-instance/InstanceAdmins.vue"
import InstanceWorkgroups from "./edit-instance/InstanceWorkgroups1.vue"

const store = useStore()
const route = useRoute()
const activeTab = ref("instance-details")
const instanceDetail = ref(null)
const locationId = route.params.locationid
const activityInstanceId = route.params.activityinstanceid

const { isReadOnlyForInstance } = usePermissions()
const readOnly = computed(() => isReadOnlyForInstance(activityInstanceId))
const { trail, setTrail } = useBreadcrumb()

// Watch for changes in the store
const storeInstanceDetail = computed(() => {
    return store.getters['Instance/instanceDetail'] || null
})

watch(storeInstanceDetail, (newValue) => {
    if (newValue) {
        instanceDetail.value = newValue
    }
}, { immediate: true })

onMounted(async () => {
    // Always fetch instance details to ensure data is loaded
    if (locationId && activityInstanceId) {
        try {
            const data = await store.dispatch("getActivityInstanceDetail", {
                locationId,
                activityinstanceid: activityInstanceId
            });
            if (data) {
                instanceDetail.value = data
                if (!trail.value.length) {
                    setTrail([
                        { label: 'Activities', icon: 'list', route: { name: 'activities-events' } },
                        { label: data.activityname || `Instance #${activityInstanceId}`, icon: 'date_range' },
                    ])
                }
            }
        } catch (error) {
            console.error("Error loading activity instance:", error);
        }
    }
})
</script>
